<template>
  <b-modal
    v-model="isOpen"
    :title="$t('managePrices')"
    centered
    size="lg"
    hide-footer
    @hidden="resetForm"
  >
    <div class="table-responsive mb-2">
      <vue-good-table
        styleClass="vgt-table striped"
        :columns="columns"
        :fixed-header="false"
        :rows="prices"
        :pagination-options="{
          enabled: false,
        }"
      >
        <div slot="emptystate" style="text-align: center">
          {{ $t("noDataForTable") }}
        </div>
        <template slot="table-row" slot-scope="props">
          <span
            class="d-flex align-items-center justify-content-center gap-2"
            v-if="props.column.field === 'action'"
          >
            <div
              v-if="!props.row.isDefault"
              class="cursor-pointer"
              :title="$t('delete')"
              @click="destroyPrice(props.row)"
            >
              <feather-icon size="16" icon="TrashIcon" />
            </div>
          </span>
          <span v-else-if="props.column.field === 'isDefault'">
            {{ props.row.isDefault ? $t("yes") : $t("no") }}
          </span>
          <span v-else-if="props.column.field === 'currency'">
            {{ (props.row.currency || "").toUpperCase() }}
          </span>
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>
      </vue-good-table>
    </div>

    <h5 class="mb-1">{{ $t("addPrice") }}</h5>
    <validation-observer ref="priceRules">
      <b-row>
        <b-col cols="12" md="6" class="mb-1">
          <div class="form-group">
            <label class="form-label input-label">
              {{ $t("currency") }}<span style="color: red">&nbsp;*</span>
            </label>
            <validation-provider
              #default="{ errors }"
              name="Currency"
              rules="required"
            >
              <multi-select
                v-model="form.currency"
                :multiple="false"
                label="label"
                track-by="country"
                :options="countryCurrencies"
                :placeholder="$t('selectOption')"
                :class="{ 'is-invalid': errors[0] }"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </div>
        </b-col>
        <b-col cols="12" md="6" class="mb-1">
          <validation-provider
            #default="{ errors }"
            name="Price"
            rules="required|positive"
          >
            <TextInput
              v-model="form.price"
              type="number"
              :min="0"
              :label="$t('price')"
              :required="true"
              :error="errors[0]"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-col>
        <b-col cols="12" md="6" class="mb-1">
          <validation-provider
            #default="{ errors }"
            name="Price Per Unit"
            rules="required|positive"
          >
            <TextInput
              v-model="form.price_per_unit"
              type="number"
              :min="0"
              :label="$t('pricePerUnit')"
              :required="true"
              :error="errors[0]"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-col>
        <b-col cols="12" md="6" class="mb-1">
          <validation-provider
            #default="{ errors }"
            name="Dentawax Price Per Unit"
            rules="required|positive"
          >
            <TextInput
              v-model="form.dentawax_price_per_unit"
              type="number"
              :min="0"
              :label="$t('dentawaxPricePerUnit')"
              :required="true"
              :error="errors[0]"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </validation-provider>
        </b-col>
      </b-row>
      <div class="d-flex align-items-center justify-content-end gap-2 mt-1">
        <b-button variant="secondary" @click="isOpen = false">
          <feather-icon icon="XIcon" size="10" />
          {{ $t("cancel") }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="isFormInvalid"
          @click="createPrice"
        >
          <feather-icon icon="PlusIcon" size="10" />
          {{ $t("create") }}
        </b-button>
      </div>
    </validation-observer>
  </b-modal>
</template>

<script>
import TextInput from "@/components/TextInput.vue";
import MultiSelect from "vue-multiselect";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, positive } from "@validations";
import countryCurrencies from "@/assets/data/country-currencies.json";

export default {
  components: {
    TextInput,
    MultiSelect,
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    plan: {
      type: Object,
      default: null,
    },
  },
  computed: {
    isOpen: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    prices() {
      return this.plan?.prices || [];
    },
    columns() {
      return [
        {
          label: this.$t("currency"),
          field: "currency",
        },
        {
          label: this.$t("price"),
          field: "price",
        },
        {
          label: this.$t("pricePerUnit"),
          field: "pricePerUnit",
        },
        {
          label: this.$t("dentawaxPricePerUnit"),
          field: "dentawaxPricePerUnit",
        },
        {
          tdClass: "vgt-center-align",
          thClass: "vgt-center-align",
          label: this.$t("action"),
          field: "action",
          sortable: false,
        },
      ];
    },
    isFormInvalid() {
      return [
        this.form.currency,
        this.form.price,
        this.form.price_per_unit,
        this.form.dentawax_price_per_unit,
      ].some((value) => value === "" || value === null || value === undefined);
    },
  },
  data() {
    return {
      countryCurrencies,
      form: {
        currency: null,
        price: "",
        price_per_unit: "",
        dentawax_price_per_unit: "",
      },
    };
  },
  methods: {
    resetForm() {
      this.form = {
        currency: null,
        price: "",
        price_per_unit: "",
        dentawax_price_per_unit: "",
      };
      this.$nextTick(() => {
        if (this.$refs.priceRules) {
          this.$refs.priceRules.reset();
        }
      });
    },
    async createPrice() {
      this.$refs.priceRules.validate().then(async (success) => {
        if (!success || !this.plan) return;
        try {
          this.$store.commit("showLoader", true);
          await this.$store.dispatch("plans/createPrice", {
            currency: this.form.currency?.currency,
            price: Number(this.form.price),
            price_per_unit: Number(this.form.price_per_unit),
            dentawax_price_per_unit: Number(this.form.dentawax_price_per_unit),
            stripePlanId: this.plan.id,
          });
          this.resetForm();
          this.$emit("updated");
        } catch (e) {
          console.error("api error ___", e);
        } finally {
          this.$store.commit("showLoader", false);
        }
      });
    },
    async destroyPrice(row) {
      if (row?.isDefault) return;

      const confirmed = await this.showConfirmationDialog({
        title: this.$t("doYouWantToDeleteThisRecord"),
        subText: this.$t("yesDeleteIt"),
      });

      if (!confirmed) return;

      try {
        this.$store.commit("showLoader", true);
        await this.$store.dispatch("plans/destroyPrice", row.id);
        this.$emit("updated");
      } catch (e) {
        console.error("api error ___", e);
      } finally {
        this.$store.commit("showLoader", false);
      }
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
